import React from 'react';

import {
  Avatar,
  Box,
  type BoxProps,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  type MenuProps,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  LuChevronDown,
  LuChevronRight,
  LuChevronUp,
  LuLogOut,
  LuUser,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Logo } from '@/components/Logo';
import { COACH_NAV_BAR_HEIGHT } from '@/features/coach/constants';
import { NavBarLanguageMenu } from '@/features/common';

import { ACCOUNT_PATH } from '../account/constants';
import { useCoachCredential } from '../coach-interview/useCoachCredential';
import { useGetCandidateQuery } from '../interview';

interface CoachNavBarAccountMenuProps extends Omit<MenuProps, 'children'> {
  name: string;
  email: string;
  isLoading: boolean;
  logoutPath: string;
  profileImage?: string;
}

export const CoachNavBarAccountMenu: React.FC<CoachNavBarAccountMenuProps> = ({
  name,
  email,
  isLoading,
  logoutPath,
  profileImage,
  ...rest
}) => {
  // const account = useGetAccountQuery();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'candidate']);

  const ACCOUNT_MENU_OPTIONS = [
    {
      title: t('auth:admin.layout.accountMenu.myProfile'),
      icon: LuUser,
      url: `${ACCOUNT_PATH}/personal-info`,
      colorScheme: 'brand',
    },
    {
      title: t('auth:admin.layout.accountMenu.logout'),
      icon: LuLogOut,
      url: logoutPath,
      colorScheme: 'gray',
    },
  ] as const;

  return (
    <Box color="codGray.900">
      <Menu placement="bottom-end" {...rest}>
        {({ isOpen }) => (
          <>
            <MenuButton
              isLoading={isLoading}
              isActive={isOpen}
              as={Button}
              variant="link"
              h={12}
              rightIcon={
                isOpen ? (
                  <Icon as={LuChevronUp} fontSize="20px" />
                ) : (
                  <Icon as={LuChevronDown} fontSize="20px" />
                )
              }
              _hover={{
                backgroundColor: 'brand.25',
              }}
            >
              <Avatar showBorder size="md" name={name} src={profileImage} />
            </MenuButton>

            <MenuList
              minWidth="17rem"
              overflow="hidden"
              p={3}
              borderColor="gray.50"
              shadow="md"
              borderRadius={12}
            >
              <MenuGroup
                title={email}
                noOfLines={1}
                m={0}
                color="gray.900"
                fontWeight="normal"
              />
              <MenuDivider />

              <VStack gap={3}>
                {ACCOUNT_MENU_OPTIONS.map((item) => (
                  <MenuItem
                    key={item.url}
                    p={1}
                    border="1px solid"
                    borderColor="gray.50"
                    transition="linear 0.1s"
                    borderRadius={8}
                    fontWeight="medium"
                    onClick={() => navigate(item.url)}
                    _hover={{
                      borderColor: `${item.colorScheme}.200`,
                      bg: `${item.colorScheme}.25`,
                    }}
                    icon={
                      <Center
                        w="2.25rem"
                        h="2.25rem"
                        bg={`${item.colorScheme}.50`}
                        borderRadius={8}
                      >
                        <Icon
                          as={item.icon}
                          fontSize="lg"
                          color={`${item.colorScheme}.500`}
                        />
                      </Center>
                    }
                  >
                    <HStack justifyContent="space-between">
                      <Text>{item.title}</Text>
                      {item.colorScheme !== 'gray' && (
                        <Icon
                          as={LuChevronRight}
                          fontSize="lg"
                          color="gray.500"
                        />
                      )}
                    </HStack>
                  </MenuItem>
                ))}
              </VStack>
            </MenuList>
          </>
        )}
      </Menu>
    </Box>
  );
};

export const CoachNavBar = (props: BoxProps) => {
  const { candidateId, token } = useCoachCredential();
  const { t } = useTranslation(['coach']);

  const { data, isLoading } = useGetCandidateQuery(
    {
      candidateId: candidateId!,
      token: token!,
    },
    {
      enabled: !!candidateId && !!token,
    }
  );

  const { name, surname, email } = data ?? {};

  const fullName = `${name ?? ''} ${surname ?? ''}`.trim();

  return (
    <Box pos="relative" w="full" {...props}>
      <Flex
        borderRadius="70%"
        pos="absolute"
        width="100%"
        bg="#371266"
        bottom={0}
        w="full"
        filter="blur(12px)"
        height="full"
        mt={5}
      />
      <Flex
        zIndex={5}
        position="fixed"
        top="0"
        insetStart="0"
        insetEnd="0"
        color="gray.50"
        align="center"
        justify="center"
        pt="safe-top"
        px="4"
        h={COACH_NAV_BAR_HEIGHT}
        bg="white"
        boxShadow={{ md: 'layout' }}
        borderBottom="1px solid transparent"
      >
        <Flex
          grow={1}
          align="center"
          justify="space-between"
          maxW="container.xl"
          blur="12px"
        >
          <HStack gap={{ md: 6, base: 3 }} align="center">
            <Logo size={7} />
            <Divider orientation="vertical" color="#BDC1C5" h="32px" />
            <Text
              color="gray.900"
              fontSize={{ base: 'sm', md: 'md' }}
              fontWeight="bold"
            >
              {t('coach:new.overview.interviewName')}
            </Text>
          </HStack>

          <HStack>
            {candidateId && token && (
              <CoachNavBarAccountMenu
                logoutPath={`/logout?redirect=${ACCOUNT_PATH}/login`}
                email={email ?? ''}
                isLoading={isLoading}
                name={fullName}
              />
            )}
            <NavBarLanguageMenu />
          </HStack>
        </Flex>
      </Flex>

      <Box h={COACH_NAV_BAR_HEIGHT} />
    </Box>
  );
};
